import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { handleUnexpectedError } from '@/app/errors';
import { useAppForm } from '@/app/lib/tanstack-form';
import { addAuthorizedUser } from '@/app/network/firefly-authenticated-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { CheckboxField, CheckboxGroup } from '@/common/components/checkbox';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@/common/components/dialog';
import { Description, Field, FieldInfo, Label } from '@/common/components/fieldset';
import { Heading } from '@/common/components/heading';
import { SettingsSidebar } from '@/settings/components/settings-sidebar';
import { authorizedUsersQueryKeys } from '@/settings/queries';
import { getAccountLabel } from '@/shared/accounts';
import { ContentSection } from '@/shared/components/content-section';
import { EmptyState, EmptyStateMessage, SleepyBulb } from '@/shared/components/empty-states';
import { Toast } from '@/shared/components/toast';
import { getMembershipLabel } from '@/shared/memberships';

export const Route = createFileRoute('/settings/authorized-users/new')({
  beforeLoad: async (ctx) => {
    // TODO: throw unauthorized if user cannot authorize users
    const user = await ctx.context.ensureUser();
    return {
      user
    };
  },
  head: () => ({
    meta: [
      { title: 'Add Authorized User | myTalquin' }
    ]
  }),
  component: NewAuthorizedUserPage
});

function NewAuthorizedUserPage() {
  const { user } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [noAuthorizationsSelectedDialogIsOpen, setNoAuthorizationsSelectedDialogIsOpen] = React.useState(false);

  const authorizedMemberships = user.memberships.filter(
    membership => membership.can_grant_to_others
  );

  const authorizedAccounts = user.accounts.filter(
    account => account.can_grant_to_others
  );

  const hasLinkableTargets = authorizedMemberships.length > 0 || authorizedAccounts.length > 0;

  const addAuthorizedUserMutation = useMutation({
    mutationFn: async (data: { email: string; memberNumbers: number[]; accountNumbers: number[] }) => {
      return addAuthorizedUser(user.id, data.email, data.memberNumbers, data.accountNumbers);
    },
    onSuccess: async () => {
      toast.success(Toast, {
        data: {
          title: 'Invitation Sent',
          content: 'An invitation has been sent to the user.'
        }
      });
      await queryClient.invalidateQueries({ queryKey: authorizedUsersQueryKeys.authorizedUsers(user.id) });

      await navigate({ to: '/settings/authorized-users' });
    },
    onError: error => handleUnexpectedError(error)
  });

  const form = useAppForm({
    defaultValues: {
      email: '',
      memberships: authorizedMemberships.map(membership => ({
        memberNumber: membership.member_number,
        authorize: false
      })),
      accounts: authorizedAccounts.map(account => ({
        accountNumber: account.account_number,
        authorize: false
      }))
    },
    validators: {
      onBlur: z.object({
        email: z.email('Please enter a valid email address.'),
        memberships: z.array(
          z.object({
            memberNumber: z.number(),
            authorize: z.boolean()
          })
        ),
        accounts: z.array(
          z.object({
            accountNumber: z.number(),
            authorize: z.boolean()
          })
        )
      })
    },
    onSubmit: async ({ value }) => {
      console.log({ value });
      const { email, memberships, accounts } = value;
      const membershipsToAuthorize = memberships.filter(it => it.authorize);
      const accountsToAuthorize = accounts.filter(it => it.authorize);

      if (membershipsToAuthorize.length === 0 && accountsToAuthorize.length === 0) {
        setNoAuthorizationsSelectedDialogIsOpen(true);
        return;
      }

      await addAuthorizedUserMutation.mutateAsync({
        email,
        memberNumbers: membershipsToAuthorize.map(it => it.memberNumber),
        accountNumbers: accountsToAuthorize.map(it => it.accountNumber)
      });
    }
  });

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <SettingsSidebar active="authorized-users"/>
      <ContentSection bg="white">
        <Heading>Add Authorized User</Heading>

        {hasLinkableTargets ? (
          <form onSubmit={
            async (event) => {
              event.preventDefault();
              event.stopPropagation();
              await form.handleSubmit(event);
            }
          }
          >
            <form.AppField name="email">
              {field => (
                <Field>
                  <Label>Email</Label>
                  <field.FormInput type="email" />
                  <FieldInfo field={field} />
                  <Description>Specifying an email address for a person who does not have an account will send them an invite to create one.</Description>
                </Field>
              )}
            </form.AppField>

            <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
              <div>
                <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-100 text-sm/6">
                  <div className="py-6 sm:flex">
                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Memberships</dt>
                    <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                      <form.AppField name="memberships" mode="array">
                        {field => (
                          <CheckboxGroup>
                            {field.state.value.map((value, idx) => {
                              const { member_number: memberNumber, alias } = authorizedMemberships.find(it => it.member_number === value.memberNumber)!;

                              return (
                                <form.AppField name={`memberships[${idx}].authorize`} key={memberNumber}>
                                  {membershipField => (
                                    <CheckboxField>
                                      <membershipField.FormCheckbox />
                                      <Label>{getMembershipLabel(memberNumber, alias)}</Label>
                                    </CheckboxField>
                                  )}
                                </form.AppField>
                              );
                            })}
                          </CheckboxGroup>
                        )}
                      </form.AppField>
                    </dd>
                  </div>

                  <div className="py-6 sm:flex">
                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Accounts</dt>
                    <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                      <form.AppField name="accounts" mode="array">
                        {field => (
                          <CheckboxGroup>
                            {field.state.value.map((value, idx) => {
                              const account = authorizedAccounts.find(it => it.account_number === value.accountNumber)!;

                              return (
                                <form.AppField name={`accounts[${idx}].authorize`} key={account.account_number}>
                                  {accountField => (
                                    <CheckboxField>
                                      <accountField.FormCheckbox />
                                      <Label>{getAccountLabel(account)}</Label>
                                    </CheckboxField>
                                  )}
                                </form.AppField>
                              );
                            })}
                          </CheckboxGroup>
                        )}
                      </form.AppField>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <ButtonGroup>
              <Button plain to="/settings/authorized-users">Cancel</Button>
              <form.Subscribe selector={state => state.isSubmitting}>
                {isSubmitting => (
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </form.Subscribe>
            </ButtonGroup>
          </form>
        ) : (
          <EmptyState>
            <SleepyBulb/>
            <EmptyStateMessage>
              No memberships or accounts have been linked yet.
            </EmptyStateMessage>
          </EmptyState>
        )}
      </ContentSection>

      <Dialog open={noAuthorizationsSelectedDialogIsOpen} onClose={setNoAuthorizationsSelectedDialogIsOpen}>
        <DialogTitle>
          No Accounts or Memberships Selected
        </DialogTitle>
        <DialogDescription>
          Please select at least one account or membership.
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setNoAuthorizationsSelectedDialogIsOpen(false)}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}
